import { useCallback, useState } from 'react';
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { MarketEventInput } from '../api/client';
import { applyRememberedJoins, forgetJoins, rememberJoins } from './waitlistJoinMemory';
import {
  NotificationSettings,
  WeatherReportSubscription,
  Promoter,
  Subscription,
  SubscriptionType,
  Venue,
  JoinWaitlistRequest,
  ClaimWaitlistCodeRequest,
  BulkJoinWaitlistRequest,
  WaitlistEntriesParams,
  UpcomingSalesParams,
  WaitlistEventStatusResponse,
  ExternalAccount,
  OtpChannel,
  SupportRequestPayload,
  ProfileUpdate,
  AccountGroupPayload,
  AccountGroupFlashBuyPayload,
  SyncAccountResult,
  SyncAllResponse,
} from '../types';
import { SyncFailedError } from '../api/syncErrors';

// Query keys
export const queryKeys = {
  profile: ['profile'] as const,
  notificationSettings: ['notificationSettings'] as const,
  weatherReportSubscription: ['weatherReportSubscription'] as const,
  venues: ['venues'] as const,
  promoters: ['promoters'] as const,
  subscriptions: ['subscriptions'] as const,
  accounts: ['accounts'] as const,
  accountGroups: ['accountGroups'] as const,
  tickets: ['tickets'] as const,
  listings: ['listings'] as const,
  crowdvoltStatus: ['crowdvoltStatus'] as const,
  crowdvoltListings: ['crowdvoltListings'] as const,
  crowdvoltEvents: ['crowdvoltEvents'] as const,
  diceSubscription: ['diceSubscription'] as const,
  diceBulkJobs: ['diceBulkJobs'] as const,
  diceBulkJob: ['diceBulkJob'] as const,
  diceMyNumbers: ['diceMyNumbers'] as const,
  diceMySMS: ['diceMySMS'] as const,
  soldoutEvents: ['soldoutEvents'] as const,
  upcomingSales: ['upcomingSales'] as const,
  waitlistEntries: ['waitlistEntries'] as const,
  marketRates: ['marketRates'] as const,
  waitlistEventStatus: ['waitlistEventStatus'] as const,
  bulkJoinJobs: ['bulkJoinJobs'] as const,
  bulkJoinJob: ['bulkJoinJob'] as const,
  dropConnections: ['dropConnections'] as const,
  supportRequests: ['supportRequests'] as const,
  plans: ['plans'] as const,
  entitlement: ['entitlement'] as const,
  usageDetail: ['usageDetail'] as const,
  upcomingInvoice: ['upcomingInvoice'] as const,
  invoices: ['invoices'] as const,
  seats: ['seats'] as const,
};

export const useProfile = () => {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.getProfile(),
    staleTime: 60000,
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProfileUpdate) => api.updateProfile(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.profile, updated);
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
};

export const useNotificationSettings = () => {
  return useQuery({
    queryKey: queryKeys.notificationSettings,
    queryFn: () => api.getNotificationSettings(),
  });
};

export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: Partial<NotificationSettings>) => api.updateNotificationSettings(settings),
    onMutate: async (settings) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notificationSettings });
      const previous = queryClient.getQueryData<NotificationSettings>(queryKeys.notificationSettings);
      queryClient.setQueryData<NotificationSettings>(queryKeys.notificationSettings, (old) => ({
        ...old!,
        ...settings,
      }));
      return { previous };
    },
    onError: (_err, _settings, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.notificationSettings, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationSettings });
    },
  });
};

export const useWeatherReportSubscription = () => {
  return useQuery({
    queryKey: queryKeys.weatherReportSubscription,
    queryFn: () => api.getWeatherReportSubscription(),
  });
};

export const useSetWeatherReportSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscribed: boolean) => api.setWeatherReportSubscription(subscribed),
    onMutate: async (subscribed) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.weatherReportSubscription });
      const previous = queryClient.getQueryData<WeatherReportSubscription>(queryKeys.weatherReportSubscription);
      queryClient.setQueryData<WeatherReportSubscription>(queryKeys.weatherReportSubscription, (old) => ({
        subscribed,
        subscribed_at: subscribed ? old?.subscribed_at ?? new Date().toISOString() : null,
      }));
      return { previous };
    },
    onError: (_err, _subscribed, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.weatherReportSubscription, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.weatherReportSubscription });
    },
  });
};

export const useSendWeatherReportNow = () => {
  return useMutation({
    mutationFn: () => api.sendWeatherReportNow(),
  });
};

export const useVenues = (params?: { city?: string; popular?: boolean; limit?: number }) => {
  return useQuery({
    queryKey: [...queryKeys.venues, params],
    queryFn: () => api.getVenues(params),
  });
};

export const usePromoters = (params?: { popular?: boolean; limit?: number }) => {
  return useQuery({
    queryKey: [...queryKeys.promoters, params],
    queryFn: () => api.getPromoters(params),
  });
};

export const useSubscriptions = () => {
  return useQuery({
    queryKey: queryKeys.subscriptions,
    queryFn: () => api.getSubscriptions(),
  });
};

export const useSubscribe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, entityId }: { type: SubscriptionType; entityId: string }) =>
      api.subscribe(type, entityId),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.subscriptions });
      
      const previousSubscriptions = queryClient.getQueryData<Subscription[]>(
        queryKeys.subscriptions
      );
      
      let entityName = 'Unknown';
      if (variables.type === 'venue') {
        const venueQueries = queryClient.getQueriesData<Venue[]>({ queryKey: queryKeys.venues });
        for (const [, venues] of venueQueries) {
          const venue = venues?.find((v) => String(v.id) === variables.entityId);
          if (venue?.name) { entityName = venue.name; break; }
        }
      } else if (variables.type === 'promoter') {
        const promoterQueries = queryClient.getQueriesData<Promoter[]>({ queryKey: queryKeys.promoters });
        for (const [, promoters] of promoterQueries) {
          const promoter = promoters?.find((p) => String(p.id) === variables.entityId);
          if (promoter?.name) { entityName = promoter.name; break; }
        }
      }
      
      if (previousSubscriptions) {
        queryClient.setQueryData<Subscription[]>(queryKeys.subscriptions, [
          ...previousSubscriptions,
          {
            id: Date.now(),
            type: variables.type,
            entity_id: variables.entityId,
            entity_name: entityName,
            entity_details: {},
            created_at: new Date().toISOString(),
          },
        ]);
      }
      
      return { previousSubscriptions };
    },
    onSuccess: async (data, variables) => {
      const currentSubscriptions = queryClient.getQueryData<Subscription[]>(
        queryKeys.subscriptions
      );
      if (currentSubscriptions) {
        const updated = currentSubscriptions.map((sub) =>
          sub.entity_id === variables.entityId &&
          sub.type === variables.type &&
          sub.id > 1000
            ? { ...sub, id: data.subscription_id, entity_name: data.entity_name || sub.entity_name }
            : sub
        );
        queryClient.setQueryData<Subscription[]>(queryKeys.subscriptions, updated);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions });
    },
    onError: (err, variables, context) => {
      if (context?.previousSubscriptions) {
        queryClient.setQueryData<Subscription[]>(
          queryKeys.subscriptions,
          context.previousSubscriptions
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions });
    },
  });
};

export const useUnsubscribe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, type }: { id: number; type: SubscriptionType }) => api.unsubscribe(id, type),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.subscriptions });
      
      const previousSubscriptions = queryClient.getQueryData<Subscription[]>(
        queryKeys.subscriptions
      );
      
      if (previousSubscriptions) {
        queryClient.setQueryData<Subscription[]>(
          queryKeys.subscriptions,
          previousSubscriptions.filter((sub) => sub.id !== id)
        );
      }
      
      return { previousSubscriptions };
    },
    onSuccess: async () => {
    },
    onError: (_err, _variables, context) => {
      if (context?.previousSubscriptions) {
        queryClient.setQueryData<Subscription[]>(
          queryKeys.subscriptions,
          context.previousSubscriptions
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions });
    },
  });
};

// Accounts are synced server-side in the background (flashtools/services/
// auto_sync.py in core). Re-reading these every few minutes while the tab is
// visible picks up what that sync wrote, and each read also tells the server
// the user is active so it keeps their accounts within ~15 minutes. React
// Query pauses the interval while the tab is hidden.
const BACKGROUND_REFRESH_MS = 5 * 60 * 1000;

export const useAccounts = () => {
  return useQuery({
    queryKey: queryKeys.accounts,
    queryFn: () => api.getAccounts(),
    retry: 2,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchInterval: BACKGROUND_REFRESH_MS,
  });
};

/**
 * Current CrowdVolt secondary-market rate for a set of DICE events (+ optional
 * cumulative cost/value). Beta-gated server-side; a non-beta user gets an empty
 * result and the feature just doesn't render. Keyed by the (sorted) event ids
 * so tickets and waitlist screens share cache for the same events.
 */
export const useMarketRates = (
  events: MarketEventInput[],
  opts?: { portfolio?: boolean; basis?: 'highest_bid' | 'lowest_ask'; enabled?: boolean },
) => {
  const ids = events.map((e) => e.dice_event_id).filter(Boolean).sort();
  const sig = `${ids.join(',')}|p=${opts?.portfolio ? 1 : 0}|b=${opts?.basis ?? 'highest_bid'}`;
  return useQuery({
    queryKey: [...queryKeys.marketRates, sig],
    queryFn: () => api.getMarketRates(events, { portfolio: opts?.portfolio, basis: opts?.basis }),
    enabled: (opts?.enabled ?? true) && ids.length > 0,
    retry: 1,
    staleTime: 60_000,        // resale floors move; treat fresh ~1 min
    refetchInterval: 90_000,
  });
};

export const useSendOTP = () => {
  return useMutation({
    mutationFn: ({ phone, platform, channel }: { phone: string; platform: string; channel?: string }) =>
      api.sendOTP(phone, platform, channel || 'sms'),
  });
};

export const useConnectWithOTP = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ phone, otp, platform, deviceId }: { phone: string; otp: string; platform: string; deviceId: string }) =>
      api.connectWithOTP(phone, otp, platform, deviceId),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: queryKeys.accounts });
      }, 100);
    },
  });
};

// Reconnect an existing DICE account by id: server already holds the phone, so
// step 1 just fires the OTP and step 2 verifies it — no phone re-entry.
export const useReconnectDiceSendOtp = () => {
  return useMutation({
    mutationFn: ({ connectionId, channel = 'sms' }: { connectionId: number; channel?: OtpChannel }) =>
      api.reconnectDiceSendOtp(connectionId, channel),
  });
};

export const useReconnectDiceVerifyOtp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, otp, deviceId }: { connectionId: number; otp: string; deviceId: string }) =>
      api.reconnectDiceVerifyOtp(connectionId, otp, deviceId),
    onSuccess: (_data, { connectionId }) => {
      // Reflect the reconnect in the UI immediately: clear the stale sync error so
      // the "Reconnect needed" badge disappears without waiting for the refetch,
      // then invalidate + refetch to confirm the authoritative server state.
      queryClient.setQueryData<ExternalAccount[]>(queryKeys.accounts, (old) =>
        old?.map((acct) =>
          acct.id === connectionId
            ? {
                ...acct,
                is_active: true,
                needs_reconnect: false,
                failed_refresh: false,
                last_sync_status: undefined,
                last_sync_error: null,
                last_sync_error_code: null,
              }
            : acct,
        ),
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
      queryClient.refetchQueries({ queryKey: queryKeys.accounts });
    },
  });
};

export const useConnectDice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ phone, password }: { phone: string; password: string }) =>
      api.connectDice(phone, password),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: queryKeys.accounts });
      }, 100);
    },
  });
};

// AXS connect is two-step: sendAxsOtp triggers an OTP, then verifyAxsOtp
// confirms the OTP and persists the connection.
export const useSendAxsOtp = () => {
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.sendAxsOtp(email, password),
  });
};

export const useVerifyAxsOtp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      email: string;
      otp: string;
      mfaRequestToken: string;
      loginHash?: string;
      browserSession?: boolean;
    }) => api.verifyAxsOtp(params),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: queryKeys.accounts });
      }, 100);
    },
  });
};

export const useConnectTAO = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      email,
      password,
      card,
    }: {
      email: string;
      password: string;
      card: string;
    }) => api.connectTAO(email, password, card),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: queryKeys.accounts });
      }, 100);
    },
  });
};

export const useDisconnectAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.disconnectAccount(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: queryKeys.accounts });
    },
  });
};

export const useSyncAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.syncAccount(id),
    // onSettled, not onSuccess: a failed sync writes last_sync_status and
    // last_sync_error_code on the account too, and the row renders both.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets });
    },
  });
};

// Three at a time: enough to finish a large account list quickly, few enough
// that AXS's per-IP refresh limit (~10-12 in a burst) isn't tripped at once.
export const SYNC_ALL_CONCURRENCY = 3;

/** The accounts the server's own sync-all would pick up. */
export const isSyncAllCandidate = (a: ExternalAccount) =>
  a.is_active && !a.is_banned && !a.failed_refresh;

/**
 * Sync every account by fanning out to `POST /accounts/<id>/sync`.
 *
 * `POST /accounts/sync-all` did the same work in one request, account after
 * account, and routinely ran 1-3 minutes. Mobile Safari drops a fetch after
 * about a minute, so the sync finished on the server while the seller saw
 * "Load failed". Each per-account request is a few seconds.
 */
export const syncAccountsInParallel = async (
  ids: number[],
  onSettled?: (done: number, total: number) => void,
): Promise<SyncAllResponse> => {
  const results: SyncAccountResult[] = [];
  let next = 0;
  let done = 0;

  const worker = async () => {
    while (next < ids.length) {
      const id = ids[next++];
      try {
        const res = await api.syncAccount(id);
        results.push({
          connection_id: id,
          platform: res.platform ?? '',
          success: true,
          tickets_synced: (res.details ?? []).reduce((n, d) => n + (d.tickets_synced || 0), 0),
          error: null,
          error_code: null,
        });
      } catch (error: any) {
        results.push({
          connection_id: id,
          platform: '',
          success: false,
          tickets_synced: 0,
          error: error?.message ?? 'Failed to sync account',
          error_code: error instanceof SyncFailedError ? error.errorCode : null,
        });
      }
      onSettled?.(++done, ids.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(SYNC_ALL_CONCURRENCY, ids.length) }, worker));

  const succeeded = results.filter((r) => r.success).length;
  const failed = results.length - succeeded;
  return {
    success: failed === 0,
    message: `${succeeded} synced, ${failed} failed`,
    total_synced: results.reduce((n, r) => n + r.tickets_synced, 0),
    succeeded,
    failed,
    results,
  };
};

export const useSyncAllAccounts = () => {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const mutation = useMutation({
    mutationFn: (accounts: ExternalAccount[]) => {
      const ids = accounts.filter(isSyncAllCandidate).map((a) => a.id);
      setProgress({ done: 0, total: ids.length });
      return syncAccountsInParallel(ids, (done, total) => setProgress({ done, total }));
    },
    // Once at the end, not per account: the accounts list is a ~200KB payload.
    onSettled: () => {
      setProgress(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets });
    },
  });
  return { ...mutation, progress };
};

// ── Account groups ────────────────────────────────────────────────────────
// Every write touches three caches: the groups themselves, the accounts list
// (which carries each account's tags) and the Flash Buy rows (which a rule
// change can flip). Invalidating all three keeps the surfaces in step.

export const useAccountGroups = () => {
  return useQuery({
    queryKey: queryKeys.accountGroups,
    queryFn: () => api.getAccountGroups(),
    staleTime: 30000,
    // A 403 here means "you are a chair without inventory.view"; retrying
    // will not change that.
    retry: false,
  });
};

const useInvalidateGroupSurfaces = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.accountGroups });
    queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    queryClient.invalidateQueries({ queryKey: queryKeys.dropConnections });
  };
};

export const useCreateAccountGroup = () => {
  const invalidate = useInvalidateGroupSurfaces();
  return useMutation({
    mutationFn: (payload: AccountGroupPayload) => api.createAccountGroup(payload),
    onSuccess: invalidate,
  });
};

export const useUpdateAccountGroup = () => {
  const invalidate = useInvalidateGroupSurfaces();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AccountGroupPayload }) =>
      api.updateAccountGroup(id, payload),
    onSuccess: invalidate,
  });
};

export const useDeleteAccountGroup = () => {
  const invalidate = useInvalidateGroupSurfaces();
  return useMutation({
    mutationFn: (id: number) => api.deleteAccountGroup(id),
    onSuccess: invalidate,
  });
};

export const useUpdateAccountGroupMembers = () => {
  const invalidate = useInvalidateGroupSurfaces();
  return useMutation({
    mutationFn: ({ id, add, remove }: { id: number; add?: number[]; remove?: number[] }) =>
      api.updateAccountGroupMembers(id, { add, remove }),
    onSuccess: invalidate,
  });
};

export const useApplyAccountGroupFlashBuy = () => {
  const invalidate = useInvalidateGroupSurfaces();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AccountGroupFlashBuyPayload }) =>
      api.applyAccountGroupFlashBuy(id, payload),
    onSuccess: invalidate,
  });
};

export const useTransferLog = (params?: Parameters<typeof api.getTransferLog>[0]) => {
  return useQuery({
    queryKey: ['transfers', params],
    queryFn: () => api.getTransferLog(params),
  });
};

export const useCheckDuplicateTransfer = () => {
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.checkDuplicateTransfer>[0]) =>
      api.checkDuplicateTransfer(payload),
  });
};

export const useDiceDropLink = () => {
  return useMutation({
    mutationFn: () => api.getDiceDropLink(),
  });
};

export const useDropConnections = () => {
  return useQuery({
    queryKey: queryKeys.dropConnections,
    queryFn: () => api.getDropConnections(),
    staleTime: 30000,
  });
};

export const useSetDropConnectionEnabled = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      api.setDropConnectionEnabled(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropConnections });
    },
  });
};

export const useSetAllDropConnectionsEnabled = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.setAllDropConnectionsEnabled(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropConnections });
    },
  });
};

export const useSetAllDropConnectionsTier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tierMatch: string | null) => api.setAllDropConnectionsTier(tierMatch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropConnections });
    },
  });
};

export const useSetDropConnectionTier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tierMatch }: { id: number; tierMatch: string | null }) =>
      api.setDropConnectionTier(id, tierMatch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropConnections });
    },
  });
};

export const useTickets = (params?: {
  status?: string;
  platform?: string;
  date_from?: string;
  date_to?: string;
  group_by?: 'event';
  include_past?: boolean;
  banned?: boolean;
}) => {
  return useQuery({
    queryKey: [...queryKeys.tickets, params],
    queryFn: () => api.getTickets(params),
    refetchInterval: BACKGROUND_REFRESH_MS,
  });
};

export const useTransferTicket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ticketId,
      recipientEmail,
      recipientName,
      message,
    }: {
      ticketId: string;
      recipientEmail: string;
      recipientName: string;
      message: string;
    }) => api.transferTicket(ticketId, recipientEmail, recipientName, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets });
    },
  });
};

export const useRequestVenue = () => {
  return useMutation({
    mutationFn: ({ name, city, notes, platform, url }: { name: string; city?: string; notes?: string; platform?: string; url?: string }) =>
      api.requestVenue(name, city, notes, platform, url),
  });
};

export const useSupportRequests = () => {
  return useQuery({
    queryKey: queryKeys.supportRequests,
    queryFn: () => api.getSupportRequests(),
    staleTime: 30000,
  });
};

export const useSubmitSupportRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['submitSupportRequest'],
    mutationFn: (payload: SupportRequestPayload) => api.submitSupportRequest(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.supportRequests });
    },
  });
};

export const useRequestPromoter = () => {
  return useMutation({
    mutationFn: ({ name, notes, platform, url }: { name: string; notes?: string; platform?: string; url?: string }) =>
      api.requestPromoter(name, notes, platform, url),
  });
};

export const useRequestArtist = () => {
  return useMutation({
    mutationFn: ({ name, notes, platform, url }: { name: string; notes?: string; platform?: string; url?: string }) =>
      api.requestArtist(name, notes, platform, url),
  });
};

export const useExportTickets = () => {
  return useMutation({
    mutationFn: (params?: {
      platform?: string;
      include_past?: boolean;
      date_from?: string;
      date_to?: string;
      status?: string;
      exclude_status?: string;
    }) => api.exportTickets(params),
  });
};

export const useValidateGiftTransfer = () => {
  return useMutation({
    mutationFn: ({
      recipientEmail,
      ticketIds,
      quantity,
    }: {
      recipientEmail: string;
      ticketIds: string[];
      quantity: number;
    }) => api.validateGiftTransfer(recipientEmail, ticketIds, quantity),
  });
};

export const useConfirmGiftTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      recipientEmail,
      externalTicketIds,
      quantity,
    }: {
      recipientEmail: string;
      externalTicketIds: string[];
      quantity: number;
    }) => api.confirmGiftTransfer(recipientEmail, externalTicketIds, quantity),
    onSuccess: () => {
      // Invalidate tickets query to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets });
    },
  });
};

export const useManualTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: Parameters<typeof api.manualTransfer>[0]) =>
      api.manualTransfer(params),
    onSuccess: (result) => {
      // A 202 only means AXS texted the seller a code — no ticket has moved, so
      // refetching here would just show the same list again.
      if (result.otp_required) return;
      // Invalidate tickets query to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets });
    },
  });
};

export const useBatchTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.batchTransfer>[0]) =>
      api.batchTransfer(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets });
    },
  });
};

// ── Listings ──────────────────────────────────────────────────────────────

export const useListings = (params?: { status?: string; event_id?: number }) => {
  return useQuery({
    queryKey: [...queryKeys.listings, params],
    queryFn: () => api.getListings(params),
  });
};

export const useUpdateListing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof api.updateListing>[1] }) =>
      api.updateListing(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.listings });
      const previous = queryClient.getQueryData([...queryKeys.listings, undefined]);
      queryClient.setQueryData([...queryKeys.listings, undefined], (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((l: any) => (l.id === id ? { ...l, ...data } : l)),
        };
      });
      return { previous };
    },
    onError: (_err: any, _vars: any, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData([...queryKeys.listings, undefined], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listings });
    },
  });
};

// ── CrowdVolt ─────────────────────────────────────────────────────────────

export const useCrowdVoltStatus = () => {
  return useQuery({
    queryKey: queryKeys.crowdvoltStatus,
    queryFn: () => api.getCrowdVoltStatus(),
  });
};

export const useCrowdVoltSendOTP = () => {
  return useMutation({
    mutationFn: (phone: string) => api.crowdVoltSendOTP(phone),
  });
};

export const useCrowdVoltVerifyOTP = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.crowdVoltVerifyOTP(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltStatus });
    },
  });
};

export const useCrowdVoltConnectSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      session_token: string;
      session_jwt?: string;
      user_id?: string;
      email?: string;
      phone?: string;
      score?: string;
    }) => api.crowdVoltConnectSession(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltStatus });
    },
  });
};

export const useDisconnectCrowdVolt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.disconnectCrowdVolt(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltStatus });
      queryClient.removeQueries({ queryKey: queryKeys.crowdvoltListings });
    },
  });
};

export const useCrowdVoltListings = (
  params?: { ft_event_id?: number; status?: string; sync?: boolean },
  enabled = true
) => {
  return useQuery({
    queryKey: [...queryKeys.crowdvoltListings, params],
    queryFn: () => api.getCrowdVoltListings(params),
    enabled,
  });
};

export const useCrowdVoltEvents = (params?: { include_unlinked?: boolean }) => {
  return useQuery({
    queryKey: [...queryKeys.crowdvoltEvents, params],
    queryFn: () => api.getCrowdVoltEvents(params),
  });
};

export const useUpdateCrowdVoltListing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ask_uqid, data }: { ask_uqid: string; data: { price?: number; qty?: number } }) =>
      api.updateCrowdVoltListing(ask_uqid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltListings });
    },
  });
};

export const useLinkCrowdVoltListing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ask_uqid, ft_listing_id, event_uqid }: { ask_uqid: string; ft_listing_id: number; event_uqid: string }) =>
      api.linkCrowdVoltListing(ask_uqid, ft_listing_id, event_uqid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltListings });
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltEvents });
      queryClient.invalidateQueries({ queryKey: queryKeys.listings });
    },
  });
};

export const useUnlinkCrowdVoltListing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ask_uqid: string) => api.unlinkCrowdVoltListing(ask_uqid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltListings });
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltEvents });
      queryClient.invalidateQueries({ queryKey: queryKeys.listings });
    },
  });
};

export const useSearchCrowdVoltEvents = (q: string, opts?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['crowdvolt', 'event-search', q],
    queryFn: () => api.searchCrowdVoltEvents(q),
    enabled: (opts?.enabled ?? true) && q.trim().length >= 2,
    staleTime: 30_000,
  });
};

export const useCrowdVoltEventTicketTypes = (event_uqid: string | null) => {
  return useQuery({
    queryKey: ['crowdvolt', 'ticket-types', event_uqid],
    queryFn: () => api.getCrowdVoltEventTicketTypes(event_uqid as string),
    enabled: !!event_uqid,
    staleTime: 60_000,
  });
};

export const useCreateDualListing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.createDualListing>[0]) =>
      api.createDualListing(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltListings });
      queryClient.invalidateQueries({ queryKey: queryKeys.crowdvoltEvents });
      queryClient.invalidateQueries({ queryKey: queryKeys.listings });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets });
    },
  });
};

// ── Dice Subscription ─────────────────────────────────────────────────────

export const useDiceSubscriptions = () => {
  return useQuery({
    queryKey: queryKeys.diceSubscription,
    queryFn: () => api.diceGetSubscriptions(),
    staleTime: 30000,
  });
};

// ── Bulk Dice account creation ────────────────────────────────────────────

export const useDiceBulkJobs = () => {
  return useQuery({
    queryKey: queryKeys.diceBulkJobs,
    queryFn: () => api.diceBulkJobs(),
    staleTime: 15000,
  });
};

/** Poll one bulk job; refetches every 3s while it is still running. */
export const useDiceBulkJob = (jobId: number | null) => {
  return useQuery({
    queryKey: [...queryKeys.diceBulkJob, jobId],
    queryFn: () => api.diceBulkJobStatus(jobId!),
    enabled: jobId != null,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'pending' || s === 'processing' ? 3000 : false;
    },
  });
};

export const useDiceBulkCreate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quantity, forwardingEmail }: { quantity: number; forwardingEmail?: string }) =>
      api.diceBulkCreate(quantity, forwardingEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceBulkJobs });
    },
  });
};

export const useDiceBulkCancel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: number) => api.diceBulkCancel(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceBulkJobs });
      queryClient.invalidateQueries({ queryKey: queryKeys.diceBulkJob });
      queryClient.invalidateQueries({ queryKey: queryKeys.diceSubscription });
    },
  });
};

export const useDiceCreateCheckout = () => {
  return useMutation({
    mutationFn: ({ forwardingEmail, diceEmail, firstName, lastName, dob }: {
      forwardingEmail: string;
      diceEmail?: string | null;
      firstName?: string;
      lastName?: string;
      dob?: string;
    }) =>
      api.diceCreateCheckout(forwardingEmail, diceEmail, { firstName, lastName, dob }),
  });
};

export const useDiceCancelPending = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: number) => api.diceCancelPending(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceSubscription });
    },
  });
};

export const useDiceCancelSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, immediate }: { subscriptionId: number; immediate: boolean }) =>
      api.diceCancelSubscription(subscriptionId, immediate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceSubscription });
    },
  });
};

export const useDiceResubscribe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: number) => api.diceResubscribe(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceSubscription });
    },
  });
};

export const useDiceUpdateEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, forwardingEmail }: { subscriptionId: number | string; forwardingEmail: string }) =>
      api.diceUpdateEmail(subscriptionId, forwardingEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceSubscription });
    },
  });
};

export const useDiceRequestOTP = () => {
  return useMutation({
    mutationFn: ({ subscriptionId, forwardingEmail, diceEmail, firstName, lastName, dob }: {
      subscriptionId: number | string;
      forwardingEmail: string;
      diceEmail?: string;
      firstName?: string;
      lastName?: string;
      dob?: string;
    }) =>
      api.diceRequestOTP(subscriptionId, forwardingEmail, diceEmail, { firstName, lastName, dob }),
  });
};

export const useDiceCompleteSignup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, otp, email, firstName, lastName, dob }: {
      subscriptionId: number | string;
      otp: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      dob?: string;
    }) =>
      api.diceCompleteSignup(subscriptionId, otp, email, { firstName, lastName, dob }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceSubscription });
      queryClient.invalidateQueries({ queryKey: queryKeys.diceMyNumbers });
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    },
  });
};

export const useDiceBillingPortal = () => {
  return useMutation({
    mutationFn: (returnUrl: string) => api.diceBillingPortal(returnUrl),
  });
};

export const useDiceMyNumbers = () => {
  return useQuery({
    queryKey: queryKeys.diceMyNumbers,
    queryFn: () => api.diceGetMyNumbers(),
    staleTime: 60000,
  });
};

export const useDiceMySMS = (limit: number = 20) => {
  return useQuery({
    queryKey: [...queryKeys.diceMySMS, limit],
    queryFn: () => api.diceGetMySMS(limit),
    staleTime: 30000,
  });
};

export const useDiceSearchNumbers = () => {
  return useMutation({
    mutationFn: (areaCode: string) => api.diceSearchNumbers(areaCode),
  });
};

export const useDicePurchaseNumber = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ phoneNumber, forwardingEmail }: { phoneNumber: string; forwardingEmail: string }) =>
      api.dicePurchaseNumber(phoneNumber, forwardingEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diceMyNumbers });
    },
  });
};

// ── Waitlist ──────────────────────────────────────────────────────────────

export const useWaitlistEntries = (params?: WaitlistEntriesParams) => {
  return useQuery({
    queryKey: [...queryKeys.waitlistEntries, params ?? null],
    queryFn: () => api.getWaitlistEntries(params),
  });
};

/** Every eligible account for one event — the picker's rows and badges. */
export const useWaitlistEventStatus = (eventId: string | null | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.waitlistEventStatus, eventId],
    queryFn: () => api.getWaitlistEventStatus(eventId!),
    enabled: !!eventId,
    select: eventId ? selectRememberedJoins(eventId) : undefined,
  });
};

/**
 * A stable `select` per event id.
 *
 * Stable matters twice over: React Query re-runs `select` whenever the function
 * identity changes, and `useQueries` builds its options array on every render,
 * so an inline arrow would recompute the overlay constantly.
 */
const rememberedJoinsSelectors = new Map<
  string,
  (data: WaitlistEventStatusResponse) => WaitlistEventStatusResponse
>();

const selectRememberedJoins = (eventId: string) => {
  const existing = rememberedJoinsSelectors.get(eventId);
  if (existing) return existing;
  const selector = (data: WaitlistEventStatusResponse) => applyRememberedJoins(data, eventId);
  rememberedJoinsSelectors.set(eventId, selector);
  return selector;
};

/**
 * Fetches one event's status on demand, outside the render path.
 *
 * The join button has to know who is already on the list before it can leave
 * them out, and it sits on collapsed cards that never mounted a picker. Doing
 * it on click keeps the list to one request per join instead of one per event
 * on screen.
 */
export const useFetchWaitlistEventStatus = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (eventId: string): Promise<WaitlistEventStatusResponse> => {
      const data = await queryClient.fetchQuery({
        queryKey: [...queryKeys.waitlistEventStatus, eventId],
        queryFn: () => api.getWaitlistEventStatus(eventId),
        // A picker opened seconds ago has already paid for this.
        staleTime: 30_000,
      });
      // fetchQuery returns the raw response — `select` only runs for
      // subscribers — so this session's joins are layered on by hand.
      return applyRememberedJoins(data, eventId);
    },
    [queryClient],
  );
};

/**
 * The same query for several events at once. The join builder needs this data
 * outside any one picker's subtree — accounts that already hold tickets are
 * dropped from the default selection, so the payload and the checkboxes have
 * to read the same source — and hooks cannot be called per event in a loop.
 */
export const useWaitlistEventStatuses = (eventIds: string[]) => {
  return useQueries({
    queries: eventIds.map((eventId) => ({
      queryKey: [...queryKeys.waitlistEventStatus, eventId],
      queryFn: () => api.getWaitlistEventStatus(eventId),
      select: selectRememberedJoins(eventId),
    })),
  });
};

/**
 * Waitlist entries only refresh server-side on an account sync, so a join we
 * just made wouldn't badge until the next one. These write the join straight
 * into the event-scoped cache so the picker reflects it immediately.
 *
 * `joined_at` is set because this genuinely IS a FlashTools join — the field
 * means exactly that, and the server will report the same once it syncs.
 */
export const useMarkWaitlistJoined = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (
      eventId: string,
      joins: Array<{
        accountId: number;
        accountLabel: string;
        ticketTypeId: number | null;
        ticketTypeName: string | null;
        numberOfTickets: number;
      }>,
    ) => {
      const now = new Date().toISOString();
      const at = Date.now();

      // Remembering is what makes the badge stick: the cache write below is
      // undone by the next refetch, which still predates the account sync.
      rememberJoins(
        eventId,
        joins.map((join) => ({
          accountId: join.accountId,
          entry: {
            status: 'joined',
            ticket_type_id: join.ticketTypeId,
            ticket_type_name: join.ticketTypeName,
            number_of_tickets: join.numberOfTickets,
            expiry_date: null,
            joined_at: now,
            source: 'flashtools',
            last_synced_at: now,
          },
        })),
        at,
      );

      queryClient.setQueryData<WaitlistEventStatusResponse>(
        [...queryKeys.waitlistEventStatus, eventId],
        (old) => {
          const base: WaitlistEventStatusResponse = old ?? {
            success: true,
            event_id: eventId,
            accounts: [],
            sync: {
              last_attempted_at: now,
              last_complete_at: now,
              stale_account_count: 0,
              stale_account_ids: [],
              account_count: 0,
            },
          };

          // Dice replaces an account's existing entry rather than stacking, so
          // the local copy replaces too — one waitlist entry per account row.
          const byAccount = new Map(base.accounts.map((a) => [a.account_id, a]));
          for (const join of joins) {
            const existing = byAccount.get(join.accountId);
            byAccount.set(join.accountId, {
              account_id: join.accountId,
              account_label: existing?.account_label ?? join.accountLabel,
              // Joining changes nothing about tickets already held, so the
              // server's count and breakdown carry over untouched.
              active_tickets: existing?.active_tickets ?? 0,
              active_ticket_types: existing?.active_ticket_types ?? [],
              waitlist: {
                status: 'joined',
                ticket_type_id: join.ticketTypeId,
                ticket_type_name: join.ticketTypeName,
                number_of_tickets: join.numberOfTickets,
                expiry_date: null,
                joined_at: now,
                source: 'flashtools',
                last_synced_at: now,
              },
            });
          }

          return { ...base, accounts: Array.from(byAccount.values()) };
        },
      );
    },
    [queryClient],
  );
};

/**
 * Drops a remembered join again.
 *
 * Nothing calls this on the join path any more — badges are written only from
 * a job's `success` items, so there is no premature claim to take back. It
 * stays for the one case that still needs it: a caller that learns a remembered
 * join was wrong after the fact.
 */
export const useUnmarkWaitlistJoined = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (eventId: string, accountIds: number[]) => {
      if (accountIds.length === 0) return;

      forgetJoins(eventId, accountIds);

      queryClient.setQueryData<WaitlistEventStatusResponse>(
        [...queryKeys.waitlistEventStatus, eventId],
        (old) => {
          if (!old) return old;
          const drop = new Set(accountIds);
          // The account row itself stays — it is a real eligible account either
          // way. Only the entry we invented is cleared, and only if we invented
          // it: a server-sourced entry is real even if this join failed.
          const accounts = old.accounts.map((a) =>
            drop.has(a.account_id) && a.waitlist?.source === 'flashtools'
              ? { ...a, waitlist: null }
              : a,
          );
          return { ...old, accounts };
        },
      );
    },
    [queryClient],
  );
};

export const useSoldoutEvents = (params?: {
  waitlist?: boolean;
  subscribed?: boolean;
  city?: string;
  limit?: number;
}) => {
  return useQuery({
    queryKey: [...queryKeys.soldoutEvents, params],
    queryFn: () => api.getSoldoutEvents(params),
  });
};

/**
 * Rows come back sorted by the earliest moment each event becomes buyable, so
 * the order is the answer — don't re-sort it downstream.
 *
 * Retries are left at the default single attempt: a 503 here means the upstream
 * Go service is down, and hammering it does not bring it back.
 */
export const useUpcomingSales = (params?: UpcomingSalesParams) => {
  return useQuery({
    queryKey: [...queryKeys.upcomingSales, params ?? null],
    queryFn: () => api.getUpcomingSales(params),
  });
};

export const useJoinWaitlist = () => {
  return useMutation({
    mutationFn: (request: JoinWaitlistRequest) => api.joinWaitlist(request),
  });
};

/**
 * Claiming is a single synchronous call, so there is no job id and nothing to
 * poll — the mutation's own pending state is the whole progress story.
 */
export const useClaimWaitlistCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ClaimWaitlistCodeRequest) => api.claimWaitlistCode(request),
    onSuccess: () => {
      // A claimed code can move the entry's status on Dice's side, and the
      // entries list is the surface showing that status.
      queryClient.invalidateQueries({ queryKey: queryKeys.waitlistEntries });
    },
  });
};

export const useBulkJoinWaitlist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: BulkJoinWaitlistRequest) => api.bulkJoinWaitlist(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bulkJoinJobs });
    },
  });
};

export const useBulkJoinJobs = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.bulkJoinJobs,
    queryFn: () => api.getBulkJoinJobs(),
    enabled: options?.enabled ?? true,
  });
};

export const useCancelBulkJoinJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: number) => api.cancelBulkJoinJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bulkJoinJob });
    },
  });
};

/**
 * Poll one bulk-join job. The interval only runs while the job is still going
 * and the last poll succeeded: a finished job has nothing new to say, and a
 * poll that failed (a 429 above all) must not be repeated twice a second —
 * one account did exactly that for three minutes on 2026-09-18 and kept its
 * own rate-limit bucket empty for everything else.
 */
export const useBulkJoinJob = (jobId: number | null, options?: { refetchInterval?: number | false }) => {
  const interval = options?.refetchInterval ?? false;
  return useQuery({
    queryKey: [...queryKeys.bulkJoinJob, jobId],
    queryFn: () => api.getBulkJoinJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      if (!interval) return false;
      if (query.state.status === 'error') return false;
      const s = query.state.data?.status;
      if (s === 'completed' || s === 'failed' || s === 'cancelled') return false;
      return interval;
    },
  });
};

// ────────────────────────────────
// Subscription billing
// ────────────────────────────────

export const usePlans = () => {
  return useQuery({
    queryKey: queryKeys.plans,
    queryFn: () => api.getPlans(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useEntitlement = () => {
  return useQuery({
    queryKey: queryKeys.entitlement,
    queryFn: () => api.getEntitlement(),
    staleTime: 30 * 1000,
  });
};

/**
 * The owner's chairs. Owner-only — a chair gets 403 — so this is mounted from
 * the Seats screen, which the nav only shows to owners.
 *
 * `retry: false` because a 403 here means "you are a chair", which retrying
 * will never change.
 */
// `enabled: false` for a seat member: the endpoint refuses them, so asking is
// a guaranteed 403 that would surface as an error somewhere it means nothing.
export const useSeats = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.seats,
    queryFn: () => api.getSeats(),
    staleTime: 30 * 1000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

export const useCreateSeat = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password, label }: { email: string; password: string; label?: string }) =>
      api.createSeat(email, password, label),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.seats });
      // A chair is $5/month on the same invoice as everything else, so the
      // billing screens are stale the moment one is added.
      qc.invalidateQueries({ queryKey: queryKeys.upcomingInvoice });
    },
  });
};

export const useRemoveSeat = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seatId: number) => api.removeSeat(seatId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.seats });
      qc.invalidateQueries({ queryKey: queryKeys.upcomingInvoice });
    },
  });
};

export const useResendSeatInvite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seatId: number) => api.resendSeatInvite(seatId),
    // A resend does not change the seat list, but it does reset the code's
    // clock — refetching keeps any "invited, not yet arrived" state honest.
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.seats }),
  });
};

export const useSetSeatPassword = () => {
  return useMutation({
    mutationFn: ({ seatId, password }: { seatId: number; password: string }) =>
      api.setSeatPassword(seatId, password),
  });
};

export const useUsageDetail = () => {
  return useQuery({
    queryKey: queryKeys.usageDetail,
    queryFn: () => api.getUsageDetail(),
    staleTime: 60 * 1000,
  });
};

export const useStartCheckout = () => {
  return useMutation({
    mutationFn: ({
      tier,
      successUrl,
      cancelUrl,
      interval,
    }: {
      tier: 'core' | 'pro' | 'reserve';
      successUrl: string;
      cancelUrl: string;
      interval?: 'month' | 'year';
    }) => api.startCheckout(tier, successUrl, cancelUrl, interval ?? 'month'),
  });
};

export const useOpenBillingPortal = () => {
  return useMutation({
    mutationFn: (returnUrl: string) => api.openBillingPortal(returnUrl),
  });
};

export const useChangePlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tier,
      interval,
      returnUrl,
    }: {
      tier: 'core' | 'pro' | 'reserve';
      interval?: 'month' | 'year';
      // Where Stripe sends the user back after collecting a card, when the
      // change needs one. Optional: omit it and the backend answers with the
      // plain error instead of a portal link.
      returnUrl?: string;
    }) => api.changePlan(tier, interval ?? 'month', returnUrl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.entitlement });
      qc.invalidateQueries({ queryKey: queryKeys.upcomingInvoice });
      qc.invalidateQueries({ queryKey: queryKeys.invoices });
    },
  });
};

export const useCancelTrial = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => api.cancelTrial(reason),
    onSuccess: res => qc.setQueryData(queryKeys.entitlement, res.entitlement),
  });
};

export const useResumeTrial = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.resumeTrial(),
    onSuccess: res => qc.setQueryData(queryKeys.entitlement, res.entitlement),
  });
};

export const useUpcomingInvoice = () => {
  return useQuery({
    queryKey: queryKeys.upcomingInvoice,
    queryFn: () => api.getUpcomingInvoice(),
    staleTime: 60 * 1000,
  });
};

export const useInvoices = (limit: number = 12) => {
  return useQuery({
    queryKey: [...queryKeys.invoices, limit] as const,
    queryFn: () => api.getInvoices(limit),
    staleTime: 60 * 1000,
  });
};
